import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockResetIcon from '@mui/icons-material/LockReset';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Tree, TreeNode } from 'react-organizational-chart';
import { apiGet, apiPost } from '../../utils/api';

interface User {
  id: number;
  username: string;
  level: number;
  managerId: number | null;
  userCrm: string | null;
  createdAt: string;
}

const LEVEL_LABELS: Record<number, string> = {
  1: 'Agent vanzari junior',
  2: 'Agent vanzari senior',
  3: 'Manager zonal',
  4: 'Manager department',
  5: 'Manager de vanzari general',
  6: 'Director general',
  7: 'CEO',
};

// Hierarchy types
interface UserNode {
  id: number;
  username: string;
  level: number;
  managerId: number | null;
  managerUsername: string | null;
  subordinatesCount: number;
  createdAt: string;
}
interface HierarchyResponse {
  hierarchy: Record<number, UserNode[]>;
  totalUsers: number;
  levels: number[];
}
interface UserTreeNode extends UserNode {
  children: UserTreeNode[];
}
const buildUserTree = (users: UserNode[]): UserTreeNode[] => {
  const userMap: Record<number, UserTreeNode> = {};
  users.forEach(u => {
    userMap[u.id] = { ...u, children: [] };
  });
  const roots: UserTreeNode[] = [];
  users.forEach(u => {
    if (u.managerId && userMap[u.managerId]) {
      userMap[u.managerId].children.push(userMap[u.id]);
    } else {
      roots.push(userMap[u.id]);
    }
  });
  return roots;
};
const renderTree = (user: UserTreeNode) => (
  <TreeNode
    label={
      <Paper
        elevation={3}
        sx={{
          p: 2,
          minWidth: 180,
          textAlign: 'center',
          border: '2px solid #1976d2',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          {user.username}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {LEVEL_LABELS[user.level] || `Level ${user.level}`}
        </Typography>
      </Paper>
    }
    key={user.id}
  >
    {user.children.map(child => renderTree(child))}
  </TreeNode>
);

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [registerForm, setRegisterForm] = useState({
    username: '',
    password: '',
    level: 1,
    managerId: null as number | null,
  });
  const [resetPassword, setResetPassword] = useState('');
  const [promoteLevel, setPromoteLevel] = useState<number>(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [currentUserLevel, setCurrentUserLevel] = useState<number>(1);
  const [allSubordinates, setAllSubordinates] = useState<User[]>([]);
  const [grantingEditId, setGrantingEditId] = useState<number | null>(null);
  const [grantEditSuccess, setGrantEditSuccess] = useState<string | null>(null);
  const [grantEditError, setGrantEditError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [crmOpen, setCrmOpen] = useState(false);
  const [crmUser, setCrmUser] = useState('');
  const [hierarchyData, setHierarchyData] = useState<HierarchyResponse | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(true);
  const [hierarchyError, setHierarchyError] = useState<string | null>(null);
  const [mainTab, setMainTab] = useState(0);

  const fetchHierarchyUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet('/auth/users/hierarchy');
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const currentUser = await apiGet('/auth/me');
      setCurrentUserLevel(currentUser.level);
      setCurrentUserId(currentUser.id);
    } catch (err) {
      console.error('Error fetching current user:', err);
      setCurrentUserLevel(1); // Default to highest level if error
      setCurrentUserId(null);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchHierarchyUsers();
  }, []);

  useEffect(() => {
    const fetchHierarchy = async () => {
      setHierarchyLoading(true);
      setHierarchyError(null);
      try {
        const json = await apiGet('/auth/hierarchy');
        setHierarchyData(json);
        
        // Populate allSubordinates with potential managers (users with higher levels than current user)
        const allUsers: User[] = [];
        json.levels.forEach((level: number) => {
          allUsers.push(...json.hierarchy[level]);
        });
        
        // Filter users that can be managers (higher level than the user being created)
        const potentialManagers = allUsers.filter(user => user.level > 1); // Level 1 is the lowest, so anyone above can be a manager
        setAllSubordinates(potentialManagers);
      } catch (err: any) {
        setHierarchyError(err.message);
      } finally {
        setHierarchyLoading(false);
      }
    };
    fetchHierarchy();
  }, []);

  let userTree: UserTreeNode[] = [];
  if (hierarchyData) {
    // Flatten all users into a single array
    const allUsers: UserNode[] = [];
    hierarchyData.levels.forEach((level: number) => {
      allUsers.push(...hierarchyData.hierarchy[level]);
    });
    userTree = buildUserTree(allUsers);
  }

  // Filter available roles based on current user's level
  const getAvailableRoles = () => {
    return Object.entries(LEVEL_LABELS)
      .filter(([level]) => Number(level) < currentUserLevel)
      .reduce(
        (acc, [level, label]) => {
          acc[level] = label;
          return acc;
        },
        {} as Record<string, string>
      );
  };

  const availableRoles = getAvailableRoles();

  // Register new user
  const handleRegister = async () => {
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await apiPost('/auth/register', registerForm);
      setActionSuccess('User registered successfully!');
      setRegisterOpen(false);
      setRegisterForm({ username: '', password: '', level: 1, managerId: null });
      fetchHierarchyUsers();
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await apiPost(`/auth/users/${selectedUser.id}/reset-password`, {
        newPassword: resetPassword,
      });
      setActionSuccess('Password reset successfully!');
      setResetOpen(false);
      setResetPassword('');
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Promote user
  const handlePromoteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await apiPost('/auth/promote', {
        userId: selectedUser.id,
        newLevel: promoteLevel,
      });
      setActionSuccess('User promoted successfully!');
      setPromoteOpen(false);
      setPromoteLevel(1);
      fetchHierarchyUsers(); // Refresh the user list
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleGrantEditPermission = async (userId: number) => {
    setGrantingEditId(userId);
    setGrantEditSuccess(null);
    setGrantEditError(null);
    try {
      await apiPost(`/task/allow-past-edit?userId=${userId}`, {});
      setGrantEditSuccess('Permisiune acordată cu succes!');
    } catch (err: any) {
      setGrantEditError(err.message || 'Eroare la acordarea permisiunii');
    } finally {
      setGrantingEditId(null);
    }
  };

  const handleSetCrmUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await apiPost(`/auth/users/${selectedUser.id}/set-crm`, { userCrm: crmUser });
      setActionSuccess('CRM user set successfully!');
      setCrmOpen(false);
      setCrmUser('');
      fetchHierarchyUsers();
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 2 }}>
            <Tab label="Gestionare Utilizatori" />
            <Tab label="Ierarhie Utilizatori" />
          </Tabs>
          {mainTab === 0 && (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="h5" fontWeight={700}>
                  Gestionare Utilizatori
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setRegisterOpen(true)}
                >
                  Register User
                </Button>
              </Box>
              {loading && <CircularProgress />}
              {error && <Alert severity="error">{error}</Alert>}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>CRM User</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          {LEVEL_LABELS[user.level] || `Level ${user.level}`}
                        </TableCell>
                        <TableCell>
                          {user.userCrm || '-'}
                        </TableCell>
                        <TableCell>
                          {new Date(user.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setSelectedUser(user);
                              setResetOpen(true);
                            }}
                            title="Reset Password"
                          >
                            <LockResetIcon />
                          </IconButton>
                          <IconButton
                            color="secondary"
                            onClick={() => {
                              setSelectedUser(user);
                              setPromoteLevel(user.level + 1);
                              setPromoteOpen(true);
                            }}
                            title="Promote User"
                            disabled={user.level >= currentUserLevel - 1}
                          >
                            <TrendingUpIcon />
                          </IconButton>
                          <IconButton
                            color="info"
                            onClick={() => handleGrantEditPermission(user.id)}
                            title="Permite editare task-uri trecute 1h"
                            disabled={grantingEditId === user.id || user.id === currentUserId}
                          >
                            <AccessTimeIcon />
                          </IconButton>
                          <IconButton
                            color="success"
                            onClick={() => {
                              setSelectedUser(user);
                              setCrmOpen(true);
                            }}
                            title="Set CRM User"
                          >
                            <AccountCircleIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {mainTab === 1 && (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <GroupIcon color="primary" fontSize="large" />
                <Typography variant="h4" component="h1" fontWeight={700}>
                  Ierarhie Utilizatori
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {hierarchyLoading && (
                <Box display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              )}
              {hierarchyError && <Alert severity="error">{hierarchyError}</Alert>}
              {hierarchyData && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Box sx={{ overflow: 'auto', minWidth: 350, maxWidth: '100vw' }}>
                    {userTree.length > 0 ? (
                      <Tree
                        lineWidth={'2px'}
                        lineColor={'#1976d2'}
                        lineBorderRadius={'8px'}
                        label={
                          <Typography variant="h6" color="primary">
                            Org Chart
                          </Typography>
                        }
                      >
                        {userTree.map(root => renderTree(root))}
                      </Tree>
                    ) : (
                      <Typography>No hierarchy data available.</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Register User Dialog */}
      <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)}>
        <DialogTitle>Register New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            value={registerForm.username}
            onChange={e =>
              setRegisterForm(f => ({ ...f, username: e.target.value }))
            }
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={registerForm.password}
            onChange={e =>
              setRegisterForm(f => ({ ...f, password: e.target.value }))
            }
          />
          <TextField
            label="Role"
            select
            fullWidth
            margin="normal"
            value={registerForm.level}
            onChange={e =>
              setRegisterForm(f => ({ ...f, level: Number(e.target.value) }))
            }
            SelectProps={{ native: true }}
          >
            {Object.entries(availableRoles).map(([level, label]) => (
              <option key={level} value={level}>
                {label}
              </option>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            margin="normal"
            value={registerForm.managerId || ''}
            onChange={e =>
              setRegisterForm(f => ({ 
                ...f, 
                managerId: e.target.value ? Number(e.target.value) : null 
              }))
            }
            SelectProps={{ native: true }}
          >
            <option value="">Select a manager</option>
            {allSubordinates.map((subordinate) => (
              <option key={subordinate.id} value={subordinate.id}>
                {subordinate.username} ({LEVEL_LABELS[subordinate.level] || `Level ${subordinate.level}`})
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={actionLoading}
          >
            {actionLoading ? 'Registering...' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>Reset Password for {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            value={resetPassword}
            onChange={e => setResetPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={actionLoading}
          >
            {actionLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promote User Dialog */}
      <Dialog open={promoteOpen} onClose={() => setPromoteOpen(false)}>
        <DialogTitle>Promote {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            label="New Role"
            select
            fullWidth
            margin="normal"
            value={promoteLevel}
            onChange={e => setPromoteLevel(Number(e.target.value))}
            SelectProps={{ native: true }}
          >
            {Object.entries(availableRoles)
              .filter(([level]) => Number(level) > (selectedUser?.level || 0))
              .map(([level, label]) => (
                <option key={level} value={level}>
                  {label}
                </option>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePromoteUser}
            disabled={actionLoading}
          >
            {actionLoading ? 'Promoting...' : 'Promote User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set CRM User Dialog */}
      <Dialog open={crmOpen} onClose={() => setCrmOpen(false)}>
        <DialogTitle>Set CRM User for {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            label="CRM User"
            fullWidth
            margin="normal"
            value={crmUser}
            onChange={e => setCrmUser(e.target.value)}
            placeholder="Enter CRM username"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSetCrmUser}
            disabled={actionLoading}
          >
            {actionLoading ? 'Setting...' : 'Set CRM User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Feedback */}
      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {actionError}
        </Alert>
      )}
      {actionSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {actionSuccess}
        </Alert>
      )}
      {grantEditError && (
        <Alert severity="error" sx={{ mt: 2 }}>{grantEditError}</Alert>
      )}
      {grantEditSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>{grantEditSuccess}</Alert>
      )}
    </Box>
  );
};

export default UserManagementPage;
